import { useEffect, useState, useRef } from "react";
import { api } from "@/lib/api";
import {
  CircleNotch,
  CheckCircle,
  XCircle,
  Plug,
  FloppyDisk,
  CaretRight,
  Key,
  EnvelopeSimple,
  MapPin,
  Clock,
  Lightning,
} from "@phosphor-icons/react";

function Section({ title, icon: Icon, children, description }) {
  return (
    <div className="bg-[#121212] border border-zinc-800 rounded-md p-5 sm:p-6">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-9 h-9 rounded bg-zinc-900 border border-zinc-800 flex items-center justify-center">
          <Icon size={18} className="text-[#CCFF00]" weight="bold" />
        </div>
        <div>
          <h2 className="font-display font-bold text-lg leading-tight">{title}</h2>
          {description && <p className="text-xs text-zinc-500 mt-0.5">{description}</p>}
        </div>
      </div>
      <div className="space-y-3">{children}</div>
    </div>
  );
}

function Field({ label, hint, children }) {
  return (
    <div>
      <label className="block text-[10px] text-zinc-500 tracking-wider uppercase font-semibold mb-1.5 font-mono">
        {label}
      </label>
      {children}
      {hint && <div className="text-[11px] text-zinc-600 mt-1">{hint}</div>}
    </div>
  );
}

function TextInput({ value, onChange, type = "text", testId, placeholder }) {
  return (
    <input
      data-testid={testId}
      type={type}
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="w-full bg-black border border-zinc-800 rounded px-3 py-2 text-sm text-white focus:border-[#CCFF00] focus:outline-none focus:ring-1 focus:ring-[#CCFF00] transition-colors font-mono"
    />
  );
}

function NumberInput({ value, onChange, min, max, testId }) {
  return (
    <input
      data-testid={testId}
      type="number"
      value={value ?? ""}
      min={min}
      max={max}
      onChange={(e) => onChange(parseInt(e.target.value) || 0)}
      className="w-full bg-black border border-zinc-800 rounded px-3 py-2 text-sm text-white focus:border-[#CCFF00] focus:outline-none focus:ring-1 focus:ring-[#CCFF00] transition-colors font-mono"
    />
  );
}

function TestButton({ label, onClick, status, testId }) {
  return (
    <div className="flex items-center gap-2">
      <button
        data-testid={testId}
        onClick={onClick}
        disabled={status === "loading"}
        className="flex items-center gap-2 px-3 py-1.5 rounded text-xs font-bold bg-zinc-900 border border-zinc-800 hover:bg-zinc-800 transition-colors disabled:opacity-50"
      >
        {status === "loading" ? <CircleNotch size={12} className="animate-spin" /> : <Plug size={12} weight="bold" />}
        {label}
      </button>
      {status === "ok" && (
        <span className="inline-flex items-center gap-1 text-xs text-[#CCFF00] font-mono">
          <CheckCircle size={14} weight="fill" /> OK
        </span>
      )}
      {status === "error" && (
        <span className="inline-flex items-center gap-1 text-xs text-red-400 font-mono">
          <XCircle size={14} weight="fill" /> Error
        </span>
      )}
    </div>
  );
}

export default function Settings() {
  const [settings, setSettings] = useState(null);
  const [courts, setCourts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [testStatus, setTestStatus] = useState({});
  const [testMessage, setTestMessage] = useState({});

  const load = async () => {
    try {
      const [s, c] = await Promise.all([api.settings(), api.courts()]);
      setSettings(s);
      setCourts(c);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const set = (key, value) => setSettings((prev) => ({ ...prev, [key]: value }));

  const handleSave = async () => {
    setSaving(true);
    try {
      // Filter out masked secret fields (containing bullet character)
      const payload = { ...settings };
      ["playtomic_password", "playtomic_manual_token", "tuya_access_secret", "resend_api_key"].forEach((k) => {
        if (typeof payload[k] === "string" && payload[k].includes("•")) {
          delete payload[k];
        }
      });
      delete payload.id;
      const updated = await api.updateSettings(payload);
      setSettings(updated);
      setSavedAt(new Date());
      setTimeout(() => setSavedAt(null), 3000);
    } catch (e) {
      alert("Error: " + (e.response?.data?.detail || e.message));
    } finally {
      setSaving(false);
    }
  };

  const runTest = async (name, fn) => {
    setTestStatus((s) => ({ ...s, [name]: "loading" }));
    setTestMessage((m) => ({ ...m, [name]: "" }));
    try {
      const r = await fn();
      setTestStatus((s) => ({ ...s, [name]: r.ok ? "ok" : "error" }));
      setTestMessage((m) => ({ ...m, [name]: r.ok ? JSON.stringify(r, null, 2) : r.error || JSON.stringify(r) }));
    } catch (e) {
      setTestStatus((s) => ({ ...s, [name]: "error" }));
      setTestMessage((m) => ({ ...m, [name]: e.response?.data?.detail || e.message }));
    }
  };

  const updateCourtSwitch = async (courtId, switchCode) => {
    await api.updateCourtMapping(courtId, switchCode);
    setCourts((cs) => cs.map((c) => (c.id === courtId ? { ...c, switch_code: switchCode } : c)));
  };

  // Debounce timers per courtId (any keystroke resets its own court's timer).
  // The UI updates OPTIMISTICALLY on every change (so the number you type or
  // click always shows up immediately) and the API call fires 500 ms after the
  // last change — that way rapid arrow clicks/typing don't spam the backend
  // (which triggers a full re-sync per call) and never lose the final value.
  const timingTimers = useRef({});
  const pendingTiming = useRef({});

  const updateCourtTiming = (courtId, payload) => {
    // 1. Optimistic UI update
    setCourts((cs) => cs.map((c) => (c.id === courtId ? { ...c, ...payload } : c)));
    // 2. Merge into pending payload for this court
    pendingTiming.current[courtId] = { ...(pendingTiming.current[courtId] || {}), ...payload };
    // 3. Reset debounce timer for this court
    if (timingTimers.current[courtId]) clearTimeout(timingTimers.current[courtId]);
    timingTimers.current[courtId] = setTimeout(async () => {
      const finalPayload = pendingTiming.current[courtId];
      delete pendingTiming.current[courtId];
      delete timingTimers.current[courtId];
      try {
        await api.updateCourtTiming(courtId, finalPayload);
      } catch (e) {
        alert("Error al guardar: " + (e.response?.data?.detail || e.message));
        // Reload authoritative state from server so the UI doesn't diverge
        try {
          const c = await api.courts();
          setCourts(c);
        } catch (_reloadErr) {
          // Best-effort reload; ignore secondary errors
        }
      }
    }, 500);
  };

  if (loading || !settings) {
    return (
      <div className="flex items-center justify-center h-96">
        <CircleNotch size={32} className="animate-spin text-zinc-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-4xl">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="text-[10px] text-zinc-500 tracking-[0.3em] uppercase font-mono mb-1">
            Sistema
          </div>
          <h1 className="font-display font-black text-3xl tracking-tight">Configuración</h1>
        </div>
        <div className="flex items-center gap-3">
          {savedAt && (
            <span className="text-xs text-[#CCFF00] font-mono inline-flex items-center gap-1">
              <CheckCircle size={14} weight="fill" /> Guardado
            </span>
          )}
          <button
            data-testid="save-settings-button"
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-2 px-4 py-2 rounded text-sm font-bold bg-[#CCFF00] text-black hover:bg-[#B3E600] active:scale-[0.97] transition-all disabled:opacity-50"
          >
            {saving ? <CircleNotch size={14} className="animate-spin" /> : <FloppyDisk size={14} weight="bold" />}
            Guardar cambios
          </button>
        </div>
      </div>

      {/* Times */}
      <Section title="Anochecer y horarios globales" icon={Clock} description="Ventana pre-anochecer común a todas las pistas. Los minutos antes/después por pista se configuran abajo individualmente.">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Field label="Encender X min antes (default)" hint="Valor por defecto si una pista no tiene timing propio">
            <NumberInput
              testId="setting-minutes-before-on"
              value={settings.minutes_before_on}
              min={0}
              max={60}
              onChange={(v) => set("minutes_before_on", v)}
            />
          </Field>
          <Field label="Apagar X min después (default)" hint="Valor por defecto si una pista no tiene timing propio">
            <NumberInput
              testId="setting-minutes-after-off"
              value={settings.minutes_after_off}
              min={0}
              max={60}
              onChange={(v) => set("minutes_after_off", v)}
            />
          </Field>
          <Field label="Ventana pre-anochecer" hint="Min antes del anochecer en que el sistema considera 'modo nocturno'">
            <NumberInput
              testId="setting-minutes-before-sunset"
              value={settings.minutes_before_sunset}
              min={0}
              max={180}
              onChange={(v) => set("minutes_before_sunset", v)}
            />
          </Field>
        </div>
      </Section>

      {/* Location */}
      <Section title="Ubicación del club" icon={MapPin} description="Para calcular automáticamente la hora del anochecer">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Field label="Latitud">
            <TextInput
              testId="setting-latitude"
              value={settings.latitude}
              onChange={(v) => set("latitude", parseFloat(v) || 0)}
            />
          </Field>
          <Field label="Longitud">
            <TextInput
              testId="setting-longitude"
              value={settings.longitude}
              onChange={(v) => set("longitude", parseFloat(v) || 0)}
            />
          </Field>
          <Field label="Zona horaria">
            <TextInput
              testId="setting-timezone"
              value={settings.timezone}
              onChange={(v) => set("timezone", v)}
            />
          </Field>
        </div>
      </Section>

      {/* Playtomic */}
      <Section title="Playtomic" icon={Key} description="Credenciales del administrador del club">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Tenant ID (ID del club)" hint="Lo puedes encontrar en la URL de manager.playtomic.io">
            <TextInput
              testId="setting-playtomic-tenant"
              value={settings.playtomic_tenant_id}
              placeholder="ej: 2ab75436-9bb0-4e9c-9a6f-b12931a9ca4a"
              onChange={(v) => set("playtomic_tenant_id", v)}
            />
          </Field>
          <Field label="Email">
            <TextInput
              testId="setting-playtomic-email"
              value={settings.playtomic_email}
              onChange={(v) => set("playtomic_email", v)}
            />
          </Field>
          <Field label="Contraseña" hint="Se almacena encriptada en el servidor. Vacío = no cambiar">
            <TextInput
              testId="setting-playtomic-password"
              type="password"
              value={settings.playtomic_password}
              onChange={(v) => set("playtomic_password", v)}
            />
          </Field>
          <Field label="Token manual (opcional)" hint="Pega un Bearer token si el login automático falla">
            <TextInput
              testId="setting-playtomic-token"
              value={settings.playtomic_manual_token}
              placeholder="Bearer token..."
              onChange={(v) => set("playtomic_manual_token", v)}
            />
          </Field>
        </div>
        <TestButton
          testId="test-playtomic-button"
          label="Probar conexión Playtomic"
          status={testStatus.playtomic}
          onClick={() => runTest("playtomic", api.testPlaytomic)}
        />
        {testMessage.playtomic && (
          <pre className="text-[10px] bg-black border border-zinc-900 rounded p-2 mt-1 max-h-40 overflow-auto font-mono text-zinc-400">
            {testMessage.playtomic}
          </pre>
        )}
      </Section>

      {/* Tuya */}
      <Section title="Tuya / SmartLife" icon={Lightning} description="Conexión a iot.tuya.com para controlar el cuadro 8-Gang">
        <div className="bg-blue-500/5 border border-blue-500/20 rounded p-3 text-xs text-zinc-300 mb-2">
          <div className="font-bold text-blue-300 mb-1">¿Cómo obtener estos datos?</div>
          <ol className="list-decimal list-inside space-y-0.5 text-zinc-400">
            <li>Crea una cuenta gratis en <a className="text-[#CCFF00] underline" href="https://iot.tuya.com" target="_blank" rel="noreferrer">iot.tuya.com</a></li>
            <li>Crea un Cloud Project tipo "Smart Home" en la región <strong>Europa</strong></li>
            <li>Vincula tu cuenta SmartLife escaneando el QR desde la app móvil → Yo → Configuración → Cuenta y Seguridad</li>
            <li>Copia <strong>Access ID</strong> y <strong>Access Secret</strong> del proyecto</li>
            <li>En la pestaña "Devices", anota el <strong>UID</strong> de tu usuario vinculado y el <strong>Device ID</strong> del cuadro de luces</li>
          </ol>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Access ID">
            <TextInput
              testId="setting-tuya-access-id"
              value={settings.tuya_access_id}
              onChange={(v) => set("tuya_access_id", v)}
            />
          </Field>
          <Field label="Access Secret">
            <TextInput
              testId="setting-tuya-access-secret"
              type="password"
              value={settings.tuya_access_secret}
              onChange={(v) => set("tuya_access_secret", v)}
            />
          </Field>
          <Field label="UID (usuario)">
            <TextInput
              testId="setting-tuya-uid"
              value={settings.tuya_uid}
              onChange={(v) => set("tuya_uid", v)}
            />
          </Field>
          <Field label="Device ID (cuadro 8-Gang)">
            <TextInput
              testId="setting-tuya-device-id"
              value={settings.tuya_device_id}
              onChange={(v) => set("tuya_device_id", v)}
            />
          </Field>
          <Field label="Base URL (data center)">
            <TextInput
              testId="setting-tuya-base-url"
              value={settings.tuya_base_url}
              onChange={(v) => set("tuya_base_url", v)}
            />
          </Field>
        </div>
        <TestButton
          testId="test-tuya-button"
          label="Probar conexión Tuya"
          status={testStatus.tuya}
          onClick={() => runTest("tuya", api.testTuya)}
        />
        {testMessage.tuya && (
          <pre className="text-[10px] bg-black border border-zinc-900 rounded p-2 mt-1 max-h-60 overflow-auto font-mono text-zinc-400">
            {testMessage.tuya}
          </pre>
        )}
      </Section>

      {/* Email source (IMAP) */}
      <Section title="Fuente de reservas: Emails (recomendado)" icon={EnvelopeSimple} description="Lee las reservas de tus emails de Playtomic vía IMAP. NO requiere API de Playtomic.">
        <div className="bg-blue-500/5 border border-blue-500/20 rounded p-3 text-xs text-zinc-300 mb-2">
          <div className="font-bold text-blue-300 mb-1">Cómo generar tu contraseña de aplicación (Outlook/Hotmail)</div>
          <ol className="list-decimal list-inside space-y-0.5 text-zinc-400">
            <li>Entra en <a className="text-[#CCFF00] underline" href="https://account.live.com/proofs/AppPassword" target="_blank" rel="noreferrer">account.live.com/proofs/AppPassword</a></li>
            <li>Inicia sesión con tu cuenta <code>dontellopadel@hotmail.com</code></li>
            <li>Pulsa "Crear una nueva contraseña de aplicación"</li>
            <li>Copia el código de 16 letras y pégalo aquí abajo</li>
            <li>Guarda los cambios y pulsa "Sincronizar" en el panel para probar</li>
          </ol>
        </div>
        <div className="flex items-center gap-3 p-2 bg-black/40 rounded border border-zinc-900 mb-2">
          <input
            data-testid="setting-email-enabled"
            type="checkbox"
            checked={!!settings.email_source_enabled}
            onChange={(e) => set("email_source_enabled", e.target.checked)}
            className="w-4 h-4"
          />
          <span className="text-sm font-bold">Usar emails como fuente de reservas (recomendado)</span>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Servidor IMAP" hint="Outlook/Hotmail: outlook.office365.com">
            <TextInput
              testId="setting-imap-host"
              value={settings.email_imap_host}
              onChange={(v) => set("email_imap_host", v)}
            />
          </Field>
          <Field label="Puerto" hint="993 (SSL) para Outlook">
            <NumberInput
              testId="setting-imap-port"
              value={settings.email_imap_port}
              min={1}
              max={65535}
              onChange={(v) => set("email_imap_port", v)}
            />
          </Field>
          <Field label="Email">
            <TextInput
              testId="setting-imap-user"
              value={settings.email_imap_user}
              placeholder="dontellopadel@hotmail.com"
              onChange={(v) => set("email_imap_user", v)}
            />
          </Field>
          <Field label="Contraseña de aplicación (16 letras)">
            <TextInput
              testId="setting-imap-password"
              type="password"
              value={settings.email_imap_password}
              onChange={(v) => set("email_imap_password", v)}
            />
          </Field>
          <Field label="Días hacia atrás a leer">
            <NumberInput
              testId="setting-imap-lookback"
              value={settings.email_lookback_days}
              min={1}
              max={30}
              onChange={(v) => set("email_lookback_days", v)}
            />
          </Field>
        </div>
      </Section>

      {/* Court mapping + per-court timing */}
      <Section title="Pistas: interruptor + horarios" icon={CaretRight} description="Asigna el interruptor del cuadro Tuya y define cuándo encender/apagar para cada pista">
        <div className="space-y-3">
          {courts.map((c) => {
            const isAmbient = c.lighting_mode === "ambient";
            return (
              <div key={c.id} className="bg-black/40 border border-zinc-900 rounded p-3 space-y-3">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div className="flex-1 min-w-[120px]">
                    <div className="font-bold text-sm">{c.name}</div>
                    <div className="text-[10px] text-zinc-500 font-mono">{c.id}</div>
                  </div>
                  <div className="flex items-center gap-2">
                    <select
                      data-testid={`court-mode-${c.id}`}
                      value={c.lighting_mode}
                      onChange={(e) => updateCourtTiming(c.id, { lighting_mode: e.target.value })}
                      className="bg-black border border-zinc-800 rounded px-2 py-1.5 text-xs text-white focus:border-[#CCFF00] focus:outline-none"
                    >
                      <option value="booking">Por reserva</option>
                      <option value="ambient">Ambiente (anochecer)</option>
                    </select>
                    <select
                      data-testid={`court-mapping-${c.id}`}
                      value={c.switch_code}
                      onChange={(e) => updateCourtSwitch(c.id, e.target.value)}
                      className="bg-black border border-zinc-800 rounded px-2 py-1.5 text-xs font-mono text-white focus:border-[#CCFF00] focus:outline-none"
                    >
                      {[1, 2, 3, 4, 5, 6, 7, 8].map((n) => (
                        <option key={n} value={`switch_${n}`}>
                          switch_{n}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {!isAmbient ? (
                  <div className="grid grid-cols-3 gap-2 pl-2 border-l-2 border-[#CCFF00]/30">
                    <div>
                      <label className="block text-[10px] text-zinc-500 uppercase font-mono mb-1">ON X min antes</label>
                      <input
                        type="number"
                        min="0"
                        max="60"
                        value={c.minutes_before_on ?? 5}
                        onChange={(e) => updateCourtTiming(c.id, { minutes_before_on: parseInt(e.target.value) || 0 })}
                        className="w-full bg-black border border-zinc-800 rounded px-2 py-1 text-xs font-mono focus:border-[#CCFF00] focus:outline-none"
                      />
                    </div>
                    <div>
                      <label className="block text-[10px] text-zinc-500 uppercase font-mono mb-1">OFF X min después</label>
                      <input
                        type="number"
                        min="0"
                        max="60"
                        value={c.minutes_after_off ?? 13}
                        onChange={(e) => updateCourtTiming(c.id, { minutes_after_off: parseInt(e.target.value) || 0 })}
                        className="w-full bg-black border border-zinc-800 rounded px-2 py-1 text-xs font-mono focus:border-[#CCFF00] focus:outline-none"
                      />
                    </div>
                    <div>
                      <label className="block text-[10px] text-zinc-500 uppercase font-mono mb-1">Pre-anochecer (min)</label>
                      <input
                        type="number"
                        min="0"
                        max="240"
                        value={c.minutes_before_sunset ?? 60}
                        onChange={(e) => updateCourtTiming(c.id, { minutes_before_sunset: parseInt(e.target.value) || 0 })}
                        className="w-full bg-black border border-zinc-800 rounded px-2 py-1 text-xs font-mono focus:border-[#CCFF00] focus:outline-none"
                      />
                    </div>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 gap-2 pl-2 border-l-2 border-orange-500/30">
                    <div>
                      <label className="block text-[10px] text-zinc-500 uppercase font-mono mb-1">ON X min antes anochecer</label>
                      <input
                        type="number"
                        min="0"
                        max="180"
                        value={c.ambient_minutes_before_sunset ?? 15}
                        onChange={(e) => updateCourtTiming(c.id, { ambient_minutes_before_sunset: parseInt(e.target.value) || 0 })}
                        className="w-full bg-black border border-zinc-800 rounded px-2 py-1 text-xs font-mono focus:border-[#CCFF00] focus:outline-none"
                      />
                    </div>
                    <div>
                      <label className="block text-[10px] text-zinc-500 uppercase font-mono mb-1">OFF X min tras última luz</label>
                      <input
                        type="number"
                        min="0"
                        max="180"
                        value={c.ambient_minutes_after_last_off ?? 15}
                        onChange={(e) => updateCourtTiming(c.id, { ambient_minutes_after_last_off: parseInt(e.target.value) || 0 })}
                        className="w-full bg-black border border-zinc-800 rounded px-2 py-1 text-xs font-mono focus:border-[#CCFF00] focus:outline-none"
                      />
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
        <div className="bg-blue-500/5 border border-blue-500/20 rounded p-3 text-xs text-zinc-300 mt-3">
          <div className="font-bold text-blue-300 mb-1">¿Qué significa cada modo?</div>
          <ul className="list-disc list-inside space-y-0.5 text-zinc-400">
            <li><strong>Por reserva</strong>: la luz se enciende y apaga según las reservas de Playtomic de esa pista (Muro, Cubierta, Central, Tenis...)</li>
            <li><strong>Ambiente (anochecer)</strong>: la luz se enciende al anochecer y se apaga cuando la última pista del club termina su sesión nocturna (Parking, Terraza...)</li>
          </ul>
        </div>
      </Section>

      {/* Email notifications */}
      <Section title="Notificaciones por email" icon={EnvelopeSimple} description="Recibe avisos cuando algo falle (usa Resend)">
        <div className="bg-blue-500/5 border border-blue-500/20 rounded p-3 text-xs text-zinc-300 mb-2">
          <div className="font-bold text-blue-300 mb-1">¿Cómo obtener una API Key de Resend?</div>
          <ol className="list-decimal list-inside space-y-0.5 text-zinc-400">
            <li>Crea una cuenta gratis en <a className="text-[#CCFF00] underline" href="https://resend.com" target="_blank" rel="noreferrer">resend.com</a> (3.000 emails/mes gratis)</li>
            <li>Ve a "API Keys" en el dashboard y crea una nueva (empieza por <code>re_</code>)</li>
            <li>El sender por defecto <code>onboarding@resend.dev</code> funciona en modo prueba</li>
          </ol>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Resend API Key">
            <TextInput
              testId="setting-resend-key"
              type="password"
              value={settings.resend_api_key}
              onChange={(v) => set("resend_api_key", v)}
            />
          </Field>
          <Field label="Email para recibir alertas">
            <TextInput
              testId="setting-notification-email"
              value={settings.notification_email}
              onChange={(v) => set("notification_email", v)}
            />
          </Field>
        </div>
        <TestButton
          testId="test-email-button"
          label="Enviar email de prueba"
          status={testStatus.email}
          onClick={() => runTest("email", api.testEmail)}
        />
      </Section>
    </div>
  );
}
